import type {
  AvailableEventView,
  EventFeeStatus,
  EventStatus,
  OrganizerRef,
  ParticipationStatus,
} from '@cuito/types';
import { apiClient } from '../lib/apiClient';
import type { Paginated } from './catalog';

/**
 * 报名与缴费的四个调用（`04-api-contract.md` §11 端点表第 7/8/9/10 行）。
 *
 * - `GET  /events/available`                可报名的**平台展会**（分页包裹）
 * - `POST /events/:id/register`             报名（**只创建报名记录**，不建存档）
 * - `GET  /participations`                  **我报名的展会**（分页包裹）
 * - `POST /participations/:id/payment-proof` 上传缴费凭证（JSON 形态 B：先传到 `payment_proofs` 再提交 URL）
 *
 * ## 平台不经手资金
 *
 * `payment-proof` **只接收一张凭证图片**：不做支付、不做代收。金额是报名时从
 * `Event.stallFeeCents` 快照来的 `feeAmountCents`，**请求体里没有金额字段**
 * ⇒ 前端无法（也不该）在这一步改写金额。
 *
 * ## 形状缺口（**待裁决 D-9**）
 *
 * 链 A 冻结的 `ParticipationView` 是 §23.3 的**名单行**（主办方视角）；
 * §11.4 的**我报名的那一行**多三个东西：`eventId` + `event`（场次摘要）+ `paymentEntry`（缴费入口）。
 * 这里就地声明 `MyParticipationItem`，作为待裁决提交。
 */

export type { AvailableEventView, ParticipationStatus, EventFeeStatus };

/** §11.4 的行内场次摘要 */
export interface MyParticipationEventRef {
  name: string;
  organizer: OrganizerRef;
  venue: string;
  dates: string[];
  status: EventStatus;
}

/**
 * 缴费入口（§11.4）。
 *
 * `available` **仅当** `status = APPROVED` 且 `feeStatus ∈ { UNPAID, REJECTED }` 时为 `true`
 * —— 前端据此渲染收款码与上传控件，**不自己拼这个条件**（那会是第二份状态机）。
 */
export interface PaymentEntry {
  available: boolean;
  stallFeeCents: number | null;
  feeWechatQrUrl: string | null;
  feeAlipayQrUrl: string | null;
  feeNote: string | null;
  uploadEndpoint: string;
}

/** `GET /participations` 的一行（§11.4） */
export interface MyParticipationItem {
  participationId: string;
  eventId: string;
  event: MyParticipationEventRef;
  status: ParticipationStatus;
  feeStatus: EventFeeStatus;
  feeAmountCents: number | null;
  paymentProofUrl: string | null;
  rejectReason: string | null;
  paymentRejectReason: string | null;
  archiveLocationId: string | null;
  appliedAt: string;
  reviewedAt: string | null;
  paymentEntry: PaymentEntry;
}

/** `POST /events/:id/register` 的 201（§11.3） */
export interface RegisterResult {
  participationId: string;
  eventId: string;
  status: ParticipationStatus;
  feeStatus: EventFeeStatus;
  feeAmountCents: number | null;
  appliedAt: string;
}

/** `POST /participations/:id/payment-proof` 的 200（§11.5） */
export interface PaymentProofResult {
  participationId: string;
  status: ParticipationStatus;
  feeStatus: EventFeeStatus;
  feeAmountCents: number | null;
  paymentProofUrl: string;
  paymentSubmittedAt: string | null;
  archiveLocationId: string | null;
}

/** 报名时必填的两个联系人字段（§11.3 的校验：`@Length(2,32)` + 手机号正则） */
export interface RegisterEventRequest {
  contactName: string;
  contactPhone: string;
  circleIntro?: string;
  note?: string;
}

export interface AvailableEventsQuery {
  page?: number;
  pageSize?: number;
}

export interface MyParticipationsQuery {
  status?: ParticipationStatus;
  feeStatus?: EventFeeStatus;
  page?: number;
  pageSize?: number;
}

/** 契约 §1.3：`pageSize` 默认 20 */
export const PARTICIPATION_PAGE_SIZE = 20;

export const participationsApi = {
  /** 可报名的平台展会（§11.2；筛选条件由服务端判：`registrationOpen` + 报名期 + 未结束） */
  available: (params: AvailableEventsQuery = {}) =>
    apiClient.get<Paginated<AvailableEventView>>('/events/available', { params }),

  /** 报名：**刻意不幂等**（重复报名是 `409 EVENT_ALREADY_REGISTERED`，A-P8-03） */
  register: (eventId: string, body: RegisterEventRequest) =>
    apiClient.post<RegisterResult>(`/events/${eventId}/register`, body),

  /** 我报名的展会（分页包裹，§11.4） */
  myList: (params: MyParticipationsQuery = {}) =>
    apiClient.get<Paginated<MyParticipationItem>>('/participations', { params }),

  /** 覆盖式幂等：凭证被驳回后重新上传即再次进入 `SUBMITTED`（§11.5） */
  submitPaymentProof: (participationId: string, paymentProofUrl: string) =>
    apiClient.post<PaymentProofResult>(`/participations/${participationId}/payment-proof`, {
      paymentProofUrl,
    }),
};
